import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import useSWRMutation from 'swr/mutation';

import {
  EmptyObjectT,
  IovationBlackboxAtomT,
  IovationPurchasedAtomT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import { apiEndpoints, applyFetchers } from '../lib';

export type PurchaseIovationRequestT = {
  black_box: string;
};

export const usePurchaseIovation = (
  iovationBlackboxAtom: IovationBlackboxAtomT,
  iovationPurchasedAtom: IovationPurchasedAtomT,
) => {
  const { blackbox } = useAtomValue(iovationBlackboxAtom);
  const [iovationPurchased, setIovationPurchased] = useAtom(iovationPurchasedAtom);
  const [blackboxError, setBlackboxError] = useState(false);

  const {
    error: purchaseError,
    isMutating,
    trigger,
  } = useSWRMutation<
    WithStatusT<void>,
    AxiosError<EmptyObjectT>,
    string,
    PurchaseIovationRequestT
  >(
    apiEndpoints.apply.purchaseIovation,
    (url, { arg }) => {
      return applyFetchers.post<PurchaseIovationRequestT, void>(url, { arg });
    },
  );

  const purchaseIovation = async (): Promise<boolean> => {
    if (iovationPurchased) {
      return true;
    }

    if (isMutating) {
      return false;
    }

    if (!blackbox) {
      setBlackboxError(true);
      datadogRum.addAction('purchaseAttemptBeforeBlackboxComplete');

      return false;
    }

    try {
      const result = await trigger({ black_box: blackbox });

      if (!result) {
        datadogRum.addAction('iovationPurchaseNoResult');

        return false;
      }

      setBlackboxError(false);
    } catch (e) {
      setBlackboxError(true);
      datadogRum.addAction('iovationPurchaseError');

      return false;
    }

    setIovationPurchased(true);

    datadogRum.addAction('iovationPurchaseSuccess');

    return true;
  };

  return {
    error: purchaseError || (blackboxError ? new Error('Blackbox value not found') : undefined),
    isMutating,
    purchaseIovation,
  };
};
